<template>
    <CardsCollection
        :items="items"
        :current-page="currentPage"
        :total="total"
        :last-page="lastPage"
        :hide-header="hideHeader"
        :is-loading="isLoading"
        @paginate="(page) => load(page)">
        <template #label>
            {{ $t( 'links.index' ) }}
        </template>

        <template #empty>
            <em class="text-muted">
                {{ $t( 'links.empty' ) }}
            </em>
        </template>

        <template #header-actions>
            <AddButton
                v-if="canCreate"
                is-small
                :disabled="isEditing"
                @click="handleAdd()" />
        </template>

        <template #card="{item}">
            <LinkForm
                v-if="isCurrent(item)"
                ref="form"
                v-model="formData"
                class="w-sm-300-px w-md-500-px w-xl-700-px mb-3"
                @submit="handleSave()"
                @cancel="handleCancel()" />

            <template v-else>
                <h4 class="card-title">
                    <DataLabel hide-icon :item="item" />
                </h4>

                <LinkContent show-publications-count :link="item" />
            </template>
        </template>

        <template #card-actions="{item}">
            <FormActions
                v-if="isCurrent(item)"
                key="form-actions"
                class="flex-sm-row"
                is-small
                :is-creating="isCreating"
                @save="submitForm()"
                @cancel="cancelForm()" />

            <LinkActions
                v-else
                key="link-actions"
                :api="api"
                :link="item"
                :disabled="isLoading || isEditing"
                @edit="handleEdit(item)"
                @destroy="load()"
                @sort="load()" />
        </template>
    </CardsCollection>
</template>

<script>
import AddButton from '@/js/components/common/buttons/AddButton';
import CardsCollection from '@/js/components/common/layout/CardsCollection';
import DataLabel from '@/js/components/common/labels/DataLabel';
import FormActions from '@/js/components/common/controls/FormActions';
import LinkActions from '@/js/components/link/LinkActions';
import LinkContent from '@/js/components/link/LinkContent';
import LinkForm from '@/js/components/link/LinkForm';

import ListMixin from '@/js/mixins/ListMixin';

export default {
    name: 'LinksList',

    components: {AddButton, CardsCollection, DataLabel, FormActions, LinkActions, LinkContent, LinkForm},

    mixins: [ListMixin],

    props: {
        api: {type: Object, required: true},
    },

    data() {
        return {
            items: [],
            pagination: this.meta || {},
            loaded: false,
            current: null,
            formData: {},
        };
    },

    computed: {
        isCreating() {
            return this.current && this.current.id === 0;
        },

        isEditing() {
            return this.current !== null;
        },

        errorMessage() {
            return this.isCreating
                ? this.$t('notifications.errors.on-create')
                : this.$t('notifications.errors.on-update');
        },
    },

    mounted() {
        this.$nextTick(() => {
            this.load();
        });
    },

    methods: {
        isCurrent(link) {
            return this.current === link;
        },

        handleAdd() {
            if (this.isEditing) {
                this.$toastr.error(this.$t('notifications.errors.wip'));
                return alert(this.$t('notifications.errors.wip'));
            }

            const link = this.api.create();

            this.items.unshift(link);
            this.handleEdit(link);
        },

        handleEdit(link) {
            if (this.isEditing) {
                this.$toastr.error(this.$t('notifications.errors.wip'));
                return alert(this.$t('notifications.errors.wip'));
            }

            this.formData = {...link};
            this.current = link;

            this.$nextTick(() => {
                this.$refs.form.formAutofocus();
            });
        },

        submitForm() {
            if (!this.isEditing) {
                return;
            }

            this.$refs.form.submit();
        },

        cancelForm() {
            if (!this.isEditing) {
                return;
            }

            this.$refs.form.cancel();
        },

        handleSave() {
            if (!this.isEditing) {
                return;
            }

            const data = {...this.formData};

            const promise = this.isCreating
                ? this.api.store(data)
                : this.api.update(this.current, data);

            promise
                .then(() => {
                    Object.assign(this.current, data);

                    if (this.isCreating) {
                        this.$toastr.success(this.$t('links.notifications.created'));
                    } else {
                        this.$toastr.success(this.$t('links.notifications.updated'));
                    }
                })
                .then(() => this.handleCancel(true))
                .then(() => this.load())
                .catch(this.handleErrors);
        },

        handleCancel(isSaving = false) {
            if (this.isCreating && !isSaving) {
                this.items.shift();
            }

            this.current = null;
            this.formData = {};
        },

        handleErrors(reason) {
            if (!reason) {
                this.$toastr.error('notifications.errors.unauthorized');
                this.$navigateTo('/', 500);

                return;
            }

            this.$toastr.error(this.errorMessage);

            if (reason && reason.errors && Array.isArray(reason.errors._errors)) {
                reason.errors._errors.forEach((message) => this.$toastr.error(message));
            }
        },
    },
};
</script>
